import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, UploadCloud, X, ArrowUp, ArrowDown, ImageOff } from "lucide-react";
import { API, fileSrc, fetchSettings } from "@/lib/api";
import { ABOUT_ICONS, ABOUT_ICON_LABELS } from "@/components/icons";

const inputCls =
  "w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors text-sm";

const EMPTY_CARD = { icon: "star", title: "", text: "" };

export default function AdminAbout({ auth }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    fetchSettings().then((s) => setForm({ ...(s.about || {}), cards: (s.about?.cards || []).map((c) => ({ ...c })) }));
  }, []);

  if (!form) return <p className="text-gray-500">იტვირთება...</p>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setCard = (i, k, val) => {
    const cards = form.cards.map((c, j) => (j === i ? { ...c, [k]: val } : c));
    setForm({ ...form, cards });
  };
  const moveCard = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= form.cards.length) return;
    const cards = [...form.cards];
    [cards[i], cards[j]] = [cards[j], cards[i]];
    setForm({ ...form, cards });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/site`, { about: form }, auth);
      toast.success("შენახულია — ცვლილებები მყისიერად ჩანს საიტზე");
    } catch {
      toast.error("შენახვა ვერ მოხერხდა");
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await axios.post(`${API}/admin/settings/about-photo`, fd, auth);
      setForm((f) => ({ ...f, photo_url: r.data.photo_url }));
      toast.success("ფოტო აიტვირთა");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "ატვირთვა ვერ მოხერხდა");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const deletePhoto = async () => {
    if (!window.confirm("წაიშალოს ფოტო? გვერდი დარჩება სუფთა ტექსტური განლაგებით.")) return;
    try {
      await axios.delete(`${API}/admin/settings/about-photo`, auth);
      setForm((f) => ({ ...f, photo_url: "" }));
      toast.success("ფოტო წაიშალა");
    } catch {
      toast.error("წაშლა ვერ მოხერხდა");
    }
  };

  return (
    <div data-testid="admin-about-section" className="space-y-8 max-w-4xl">
      <section className="card-lux p-6 space-y-4">
        <h2 className="text-lg font-semibold text-white">მთავარი ტექსტები</h2>
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">გვერდის სათაური (H1)</label>
          <input data-testid="about-heading-input" value={form.heading || ""} onChange={set("heading")} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">შესავალი ტექსტი</label>
          <textarea data-testid="about-intro-input" rows={4} value={form.intro || ""} onChange={set("intro")} className={`${inputCls} resize-y`} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">CTA სათაური</label>
            <input data-testid="about-cta-title-input" value={form.cta_title || ""} onChange={set("cta_title")} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">CTA ტექსტი</label>
            <input data-testid="about-cta-text-input" value={form.cta_text || ""} onChange={set("cta_text")} className={inputCls} />
          </div>
        </div>
      </section>

      <section className="card-lux p-6 space-y-4" data-testid="about-photo-section">
        <h2 className="text-lg font-semibold text-white">გვერდის ფოტო</h2>
        <p className="text-xs text-gray-500">ფოტოს გარეშე გვერდი რჩება სუფთა ტექსტური განლაგებით — არავითარი ცარიელი ჩარჩო არ გამოჩნდება.</p>
        <div className="flex flex-wrap items-center gap-4">
          <div className="h-28 w-44 rounded-xl overflow-hidden bg-[#0D0F14] border border-white/10 flex items-center justify-center">
            {form.photo_url ? (
              <img src={fileSrc(form.photo_url)} alt="ჩვენ შესახებ — ფოტოს პრევიუ" className="w-full h-full object-cover" data-testid="about-photo-preview" />
            ) : (
              <ImageOff className="w-6 h-6 text-gray-600" />
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={() => fileInput.current?.click()} disabled={uploading} data-testid="about-photo-upload-btn" className="btn-ghost !py-2 !px-4 text-xs">
              <UploadCloud className="w-3.5 h-3.5" /> {uploading ? "იტვირთება..." : form.photo_url ? "ფოტოს შეცვლა" : "ფოტოს ატვირთვა"}
            </button>
            {form.photo_url && (
              <button onClick={deletePhoto} data-testid="about-photo-delete-btn" className="!py-2 !px-4 text-xs inline-flex items-center gap-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> წაშლა
              </button>
            )}
          </div>
          <input type="file" accept="image/*" hidden ref={fileInput} data-testid="about-photo-input" onChange={(e) => uploadPhoto(e.target.files[0])} />
        </div>
      </section>

      <section className="card-lux p-6 space-y-4" data-testid="about-cards-section">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">უპირატესობების ბარათები ({form.cards.length})</h2>
          <button onClick={() => setForm({ ...form, cards: [...form.cards, { ...EMPTY_CARD }] })} data-testid="about-add-card-btn" className="btn-gold !py-2 !px-4 text-xs">
            <Plus className="w-3.5 h-3.5" /> ბარათის დამატება
          </button>
        </div>
        {form.cards.map((c, i) => (
          <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-white/10 space-y-3" data-testid={`about-card-${i}`}>
            <div className="flex items-center gap-3">
              <select data-testid={`about-card-icon-${i}`} value={c.icon} onChange={(e) => setCard(i, "icon", e.target.value)} className={`${inputCls} !w-auto`}>
                {Object.entries(ABOUT_ICON_LABELS).map(([k, v]) => (
                  <option key={k} value={k} className="bg-[#141721]">{v}</option>
                ))}
              </select>
              <input data-testid={`about-card-title-${i}`} value={c.title} onChange={(e) => setCard(i, "title", e.target.value)} className={inputCls} placeholder="ბარათის სათაური" />
              <div className="flex gap-1 shrink-0">
                <button onClick={() => moveCard(i, -1)} disabled={i === 0} data-testid={`about-card-up-${i}`} className="w-8 h-8 rounded-lg bg-white/5 text-gray-300 flex items-center justify-center disabled:opacity-30 hover:bg-white/10 transition-colors">
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => moveCard(i, 1)} disabled={i === form.cards.length - 1} data-testid={`about-card-down-${i}`} className="w-8 h-8 rounded-lg bg-white/5 text-gray-300 flex items-center justify-center disabled:opacity-30 hover:bg-white/10 transition-colors">
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setForm({ ...form, cards: form.cards.filter((_, j) => j !== i) })}
                  data-testid={`about-card-delete-${i}`}
                  className="w-8 h-8 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 flex items-center justify-center hover:bg-red-500/20 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <textarea data-testid={`about-card-text-${i}`} rows={2} value={c.text} onChange={(e) => setCard(i, "text", e.target.value)} className={`${inputCls} resize-none`} placeholder="ბარათის აღწერა" />
          </div>
        ))}
      </section>

      <button onClick={save} disabled={saving} data-testid="about-save-btn" className="btn-gold !py-3 !px-8 text-sm disabled:opacity-60 sticky bottom-4">
        {saving ? "ინახება..." : "შენახვა"}
      </button>
    </div>
  );
}
